import './MLScan.module.scss';
import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import { useHistory, useParams } from 'react-router';
import updateProject from 'actions/project/update';
import { useReduxAction } from 'hooks/useReduxAction';
import { useRedux } from 'hooks/useRedux';
import { useTranslate } from 'hooks/useTranslate';
import { ProjectDetailsContext } from 'pages/Workspace/ProjectDetails/ProjectDetails';
import Webcam from 'react-webcam';
import { GlobalScanContext } from 'utilities/GlobalContext/GlobalScan';
import indexActivity from 'actions/activity/index';
import Token from 'components/UI/Token/Token';
import { AnimatePresence } from 'framer-motion';
import ScanInfoModal from './components/ScanInfoModal';

export default function MLScan() {
  const { rootProject } = useContext(ProjectDetailsContext);

  const translate = useTranslate();

  const userId = useRedux((state) => state.authentication?.user?.id);

  const [isChecked, setIsChecked] = useState(!!rootProject?.is_public);

  const [randomToken, setRandomToken] = useState<ActivityEntity>();
  const [tokenInterval, setTokenInterval] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [emptyProject, setEmptyProject] = useState(false);
  const [showModal, setShowModal] = useState(true);
  const [notif, setNotif] = useState('');
  const [groowy, setGroowy] = useState<ActivityEntity>();
  const imageFile = useRef(null);
  const webcamRef = useRef(null);
  const value = useContext(GlobalScanContext);

  const dispatch = useReduxAction();

  useEffect(() => {
    dispatch(indexActivity());
  }, []);

  const { projectId } = useParams() as {
    projectId: string;
  };

  const history = useHistory();

  const activities = useRedux((state) => {
    const data = state.activity?.entities.allIds
      .map((id) => state.activity?.entities.byId[id])
      .filter((a) => a?.type === 'regular')
      // filter vrije activiteiten duplicates
      .filter(
        (value, i, arr) =>
          arr.findIndex((activity) => activity?.name === value?.name) === i
      )
      .filter((activity) => activity?.slug !== 'onbekend');

    return data;
  });

  const phases = useRedux((state) => {
    const data = state.phase?.entities.allIds.map(
      (id) => state.phase?.entities.byId[id]
    );

    return data;
  });

  // Screenshot to useable blob entity
  function dataURLtoBlob(dataurl: string) {
    if (!dataurl) return;

    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  function notification(text: string) {
    setNotif(text);

    setTimeout(() => {
      setNotif('');
    }, 5000);
  }

  // Get random token for animation
  const getRandomToken = () => {
    const tokenLength = activities?.length - 2;
    const randomIndex = parseInt(Math.random() * tokenLength) + 1;
    let token = activities?.find((activity) => activity?.id === randomIndex);
    console.log(token?.slug);
    if (!token?.slug)
      token = activities?.find((activity) => activity?.slug === 'groowy');
    return token;
  };

  const onFileUpload = (imageSrc: string) => {
    setIsLoading(true);

    const tokenInterval = setInterval(() => {
      setRandomToken(getRandomToken());
    }, 600);
    setTokenInterval(tokenInterval);

    const ImageURL = imageSrc;
    const blob = dataURLtoBlob(ImageURL);

    dispatch(
      updateProject({
        processMlImage: true,
        id: projectId,
        image: blob,
      })
    )
      .then((project: ProjectProjectEntity) => {
        console.log(project);
        clearInterval(tokenInterval);
        setIsLoading(false);
        // No process recognised
        if (project.process?.length === 0) {
          console.log('empty project');
          setEmptyProject(true);
          notification(`${translate('scan.error.process-not-recognized')}`);
          console.log(
            `height: ${window.innerHeight} width: ${window.innerWidth}`
          );
        }
        // Came from side nav so not linked with existing theme
        else if (projectId === 'noTheme') {
          console.log(project.id);
          console.log(project.theme_id);
          value.setImage(blob);
          value.setImageTaken(true);
          value.setProjectId(project.id);

          clearInterval(tokenInterval);
          setIsLoading(false);
          history.push(`/select-theme`);
        }
        // Came from existing theme
        else {
          value.setSaveIsEditable(true);
          history.push(`/werkplaats/project-details/${project.id}`);
        }
      })
      .catch((error) => {
        clearInterval(tokenInterval);
        setIsLoading(false);
        notification(`${translate('scan.error.process-not-recognized')}`);
        console.log(error);
      });
  };

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    onFileUpload(imageSrc);
    setRandomToken(getRandomToken());
  }, [webcamRef]);

  const findToken = (slug: string) => {
    let token = activities?.find((activity) => activity?.slug === slug);

    if (!token) {
      token = phases?.find((phase) => phase?.slug === slug);
    }

    return token;
  };

  const videoConstraints = {
    width: 1920,
    height: 1080,
    facingMode: { ideal: 'environment' },
  };

  useEffect(() => {
    const groowyToken = findToken('groowy');
    setGroowy(groowyToken);
  }, []);

  return (
    <div styleName="scanner">
      <div styleName="click-field" onClick={capture} />
      {notif && (
        <div styleName="notification-container">
          <div
            styleName="groowy"
            style={{ backgroundImage: `url(${groowy && groowy.thumbnail})` }}
          />
          <div styleName="notifications">{`"${notif}"`}</div>
        </div>
      )}
      <div styleName="frame">
        <div styleName="tokens">
          {randomToken && 'thumbnail' in randomToken && isLoading && (
            <Token token={randomToken} />
          )}
        </div>
        <div styleName="top-left">
          <img src="/images/hoek.svg" alt="top left corner" />
        </div>
        <div styleName="top-right">
          <img src="/images/hoek.svg" alt="top right corner" />
        </div>
        <div styleName="bottom-left">
          <img src="/images/hoek.svg" alt="bottom left corner" />
        </div>
        <div styleName="bottom-right">
          <img src="/images/hoek.svg" alt="bottom right corner" />
        </div>
      </div>
      <Webcam
        audio={false}
        ref={webcamRef}
        screenshotFormat="image/jpeg"
        videoConstraints={videoConstraints}
      />
      <AnimatePresence>
        {showModal && <ScanInfoModal setModalIsOpen={setShowModal} />}
      </AnimatePresence>
    </div>
  );
}
